import { useState, useEffect, useMemo, useCallback } from "react";
import moment from "moment";
import { getDataWithComp, getAlert, formatCurrency } from "../../../../utils/SamuiUtils";
import { SUMMARY_FIELDS, DETAIL_FIELDS } from "./Fields";

export default function useDebtorReport({
  startDate,
  endDate,
  search,
  sortField,
  sortOrder,
  page,
  perPage,
  statusFilter,   // "" | "จ่ายครบ" | "ค้างจ่าย"
}) {
  const [rawData, setRawData] = useState([]);
  const [detailData, setDetailData] = useState([]);
  const [detailInfo, setDetailInfo] = useState({
    index: 0,
    code: "",
    name: "",
  });

  // โหลดข้อมูลทั้งหมด
  useEffect(() => {
    getDataWithComp("API_0701_SO_H", "ORDER BY Doc_No DESC")
      .then(setRawData)
      .catch((e) => getAlert("FAILED", e.message));
  }, []);

  // 1) กรองตามช่วงวันที่
  const byDate = useMemo(
    () =>
      rawData.filter((i) => {
        const d = moment(i.Doc_Date).format("YYYY-MM-DD");
        return d >= startDate && d <= endDate;
      }),
    [rawData, startDate, endDate]
  );

  // 2) สรุปยอดรวม, ยอดชำระ, ยอดค้างชำระ ต่อ AR_Code
  const aggregated = useMemo(() => {
    const map = {};
    byDate.forEach((item) => {
      const key = item.AR_Code;
      if (!map[key]) {
        map[key] = {
          AR_Code: item.AR_Code,
          AR_Name: item.AR_Name,
          NetTotalRaw: 0,
          paidRaw: 0,
          unpaidRaw: 0,
        };
      }
      const amt = Number(item.NetTotal) || 0;
      map[key].NetTotalRaw += amt;

      if (item.DocStatusPaid_Name === "จ่ายครบ") {
        map[key].paidRaw += amt;
      } else if (item.DocStatusPaid_Name === "ค้างจ่าย") {
        map[key].unpaidRaw += amt;
      }
    });

    return Object.values(map).map((row) => ({
      AR_Code: row.AR_Code,
      AR_Name: row.AR_Name,
      NetTotalRaw: row.NetTotalRaw,
      paidRaw: row.paidRaw,
      unpaidRaw: row.unpaidRaw,
      NetTotal: formatCurrency(row.NetTotalRaw),
      paid: formatCurrency(row.paidRaw),
      unpaid: formatCurrency(row.unpaidRaw),
    }));
  }, [byDate]);

  // 3) กรองตามสถานะและคำค้น
  const filtered = useMemo(() => {
    let arr = aggregated;
    if (statusFilter === "จ่ายครบ") {
      arr = arr.filter((i) => i.unpaidRaw === 0);
    } else if (statusFilter === "ค้างจ่าย") {
      arr = arr.filter((i) => i.unpaidRaw > 0);
    }
    if (search) {
      const q = search.toLowerCase();
      arr = arr.filter((i) =>
        [i.AR_Code, i.AR_Name].join(" ").toLowerCase().includes(q)
      );
    }
    return arr;
  }, [aggregated, statusFilter, search]);

  // 4) เรียงลำดับ
  const sorted = useMemo(() => {
    if (!sortField) return filtered;
    return [...filtered].sort((a, b) => {
      const va = a[sortField] ?? "",
        vb = b[sortField] ?? "";
      if (!isNaN(+va) && !isNaN(+vb)) {
        return sortOrder === "asc" ? va - vb : vb - va;
      }
      return sortOrder === "asc"
        ? va.toString().localeCompare(vb)
        : vb.toString().localeCompare(va);
    });
  }, [filtered, sortField, sortOrder]);

  const total = sorted.length;
  const pages = Math.ceil(total / perPage);
  const current = useMemo(
    () => sorted.slice((page - 1) * perPage, page * perPage),
    [sorted, page, perPage]
  );

  // 5) สรุปยอดรวมทั้งหมดจากตัวเลขดิบ
  const sums = useMemo(() => {
    const sumNet    = aggregated.reduce((s, i) => s + i.NetTotalRaw, 0);
    const sumPaid   = aggregated.reduce((s, i) => s + i.paidRaw, 0);
    const sumUnpaid = aggregated.reduce((s, i) => s + i.unpaidRaw, 0);
    return {
      sumNetTotal: formatCurrency(sumNet),
      sumPaid:     formatCurrency(sumPaid),
      sumUnpaid:   formatCurrency(sumUnpaid),
    };
  }, [aggregated]);

  // 6) ดึงข้อมูลรายละเอียดลูกค้าแต่ละราย
  const showDetailByIndex = useCallback(
    (idx) => {
      const cust = current[idx];
      if (!cust) return;
      const filter = [
        `AND AR_Code = '${cust.AR_Code}'`,
        `AND Doc_Date >= '${startDate}'`,
        `AND Doc_Date <= '${endDate}'`,
        "ORDER BY Doc_Date",
      ].join(" ");
      getDataWithComp("API_0701_SO_H", filter)
        .then((list) => {
          setDetailData(list || []);
          setDetailInfo({
            index: idx,
            code: cust.AR_Code,
            name: cust.AR_Name,
          });
        })
        .catch((e) => getAlert("FAILED", e.message));
    },
    [current, startDate, endDate]
  );

  return {
    current,
    total,
    pages,
    sums,
    detailData,
    detailInfo,
    showDetailByIndex,
  };
}
